var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
/**
 * @module cercaliagl/layer/Base
 */
import EventEmitter from 'eventemitter3';
import { binarySearchZIndex } from '../util.js';
/**
 * @typedef {Object} Options
 * @property {string} id Layer id
 * @property {string} [beforeId] Before layer id
 * @property {import('../Map').default} map Mapbox Map
 * @property {string} type Type
 */
/**
 * @typedef {Base|import("./WMS").default|import("./TrafficForecast").default|import("./TrafficLive").default|import("./XYZ").default} LayerLike
 * @api
 */
/**
 * @enum {string}
 */
export var LayersId = {
    /**
     * Layer buildings id.
     * @api
     */
    BUILDINGS3D: 'buildings3d',
    /**
     * Layer landmarks id.
     * @api
     */
    LANDMARKS3D: '3dlandmarks',
    /**
     * Layer landmarks model id.
     * @api
     */
    LANDMARKS3DMODELS: '3dlandmarks_models',
    /**
     * Default marker layer id.
     * @api
     */
    MARKERS_LAYER_ID: 'layerMarkers',
    /**
     * Default marker layer id.
     * @api
     */
    FEATURE_LAYER_ID: 'layerFeatures',
};
/**
 * @enum {string}
 */
export var EventType = {
    ADD_TO_MAP: 'addtomap',
};
/**
 * @enum {string}
 */
export var LayerType = {
    SYMBOL: 'symbol',
    FEATURE: 'feature',
};
/**
 * @classdesc
 * Abstract base class, used only for creating subclasses. Don't use it.
 * @api
 */
var Base = /** @class */ (function (_super) {
    __extends(Base, _super);
    /**
     * @constructor
     * @param {Options} options Layer options
     */
    function Base(options) {
        var _this = _super.call(this) || this;
        /**
         * @private
         * @type {string}
         */
        _this.id_ = options.id;
        /**
         * @private
         * @type {string}
         */
        _this.beforeId_ = options.beforeId;
        /**
         * @private
         * @type {string}
         */
        _this.type_ = options.type;
        /**
         * @private
         * @type {import("../Map").default}
         */
        _this.cercaliaMap_ = null;
        /**
         * @private
         * @type {import("maplibre-gl").Map}
         */
        _this.map_ = null;
        /**
         * @private
         * @type {HTMLElement}
         */
        _this.canvas_ = null;
        /**
         * @private
         * @type {GeoJSON|null}
         */
        _this.geojson_ = {
            type: 'FeatureCollection',
            features: [],
        };
        /**
         * @private
         * @type {import("maplibre-gl").Source}
         */
        _this.source_ = null;
        /**
         * @private
         * @type {number}
         */
        _this.timeoutId_ = null;
        /**
         * @private
         * @type {string}
         */
        _this.geojsonId_ = _this.id_;
        /**
         * @protected
         * @type {Array.<import("../Marker").default|import("../Feature").default>}
         */
        _this.featuresMouseOvered = [];
        /**
         * @private
         * @type {Array.<string>}
         */
        _this.layersId_ = [];
        return _this;
    }
    /**
     * @protected
     * @param {import("../MapStyler").StyleLayerJSON}
     */
    Base.prototype.addLayer = function (layerOptions) {
        this.layersId_.push(layerOptions.id);
        if (this.map_.getLayer(this.beforeId_)) {
            this.map_.addLayer(layerOptions, this.beforeId_);
        }
        else {
            this.map_.addLayer(layerOptions);
        }
    };
    /**
     * @protected
     * @param {string} layerId Remove layer id
     */
    Base.prototype.removeLayer = function (layerId) {
        var i = this.layersId_.indexOf(layerId);
        if (i !== -1) {
            this.layersId_.splice(i, 1);
            this.map_.removeLayer(layerId);
        }
        else {
            console.warn("removeLayer -Layer ".concat(i, " not found")); // eslint-disable-line no-console
        }
    };
    /**
     * Remove all internal mapboxgl layers.
     */
    Base.prototype.removeLayers = function () {
        var _this = this;
        this.layersId_.forEach(function (layerId) {
            _this.map_.removeLayer(layerId);
        });
        this.layersId_ = [];
        if (this.map_.getSource(this.id_)) {
            this.map_.removeSource(this.id_);
            this.source = null;
        }
    };
    /**
     * Obtain layer id.
     * @return {string} Layer id.
     * @api
     */
    Base.prototype.getId = function () {
        return this.id_;
    };
    /**
     * Return layer type.
     * @return {string} Layer type. Available values: `symbol`, `feature`.
     */
    Base.prototype.getType = function () {
        return this.type_;
    };
    /**
     * Damos un timeout antes de ejecutar setData por no ejecutarlo cada vez
     * que hay un nuevo marker
     * @param {number} [timeout] Timeout time
     */
    Base.prototype.refreshGeoJSON = function (timeout) {
        var _this = this;
        if (timeout === void 0) { timeout = 0; }
        if (this.timeoutId_) {
            clearTimeout(this.timeoutId_);
            this.timeoutId_ = null;
        }
        this.timeoutId_ = setTimeout(function () { return _this.updateGeoJSON(); }, timeout);
    };
    /**
     * @protected
     * @param {import("../BaseFeature").default} featureOrMarker Feature or marker
     */
    Base.prototype.updateFeatureGeoJSON = function (featureOrMarker) {
        var geojson = featureOrMarker.getGeoJSON();
        var i = this.getFeatureIndex_(geojson.properties.id);
        if (i !== -1) {
            var currentElement = this.geojson_.features[i];
            if (featureOrMarker.getZIndex() && featureOrMarker.getZIndex() !== currentElement.properties.zIndex) {
                this.geojson_.features.splice(i, 1);
                var geojsonFeaturesLength = this.geojson_.features.length;
                if (geojsonFeaturesLength >= 1) {
                    var positionToInsert = binarySearchZIndex(this.geojson_.features, featureOrMarker.getZIndex(), 0, geojsonFeaturesLength - 1);
                    this.geojson_.features.splice(positionToInsert, 0, geojson);
                }
                else {
                    this.geojson_.features.push(geojson);
                }
            }
            else {
                this.geojson_.features[i] = geojson;
            }
        }
        else {
            console.warn("Feature ".concat(i, " not found")); // eslint-disable-line no-console
        }
    };
    /**
     * Update GeoJSON
     */
    Base.prototype.updateGeoJSON = function () {
        this.source_.setData(this.geojson_);
    };
    /**
     * @param {import("../Map").default} map Map
     */
    Base.prototype.setMap = function (map) {
        this.cercaliaMap_ = map;
        this.map_ = this.cercaliaMap_.getMapboxglMap();
        this.canvas_ = this.map_.getCanvas();
        this.emit(EventType.ADD_TO_MAP, this.map_);
    };
    /**
     * @private
     * @param {string|number} id Id
     */
    Base.prototype.getFeatureIndex_ = function (id) {
        for (var i = 0; i < this.geojson_.features.length; i += 1) {
            var feature = this.geojson_.features[i];
            if (feature.properties.id === id) {
                return i;
            }
        }
        return -1;
    };
    /**
     * @protected
     * @param {string} id Marker id
     * @return {number} Index Return index if deleted, else returns -1.
     */
    Base.prototype.removeFeatureGeoJSON = function (id) {
        var i = this.getFeatureIndex_(id);
        if (i !== -1) {
            this.geojson_.features.splice(i, 1);
        }
        return i;
    };
    /**
     * @return {JSON} JSON Object.
     */
    Base.prototype.getGeoJSON = function () {
        return this.geojson_;
    };
    /**
     * Get internal mapboxgl layers id.
     * @return {Array.<string>}
     */
    Base.prototype.getLayersId = function () {
        return this.layersId_;
    };
    return Base;
}(EventEmitter));
export default Base;
//# sourceMappingURL=Base.js.map